import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import useSeo from '@/hooks/useSeo';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/da2j8tbd3r93mn1k39ug';

const contactInfo = [
  { icon: 'ri-mail-line', title: '邮箱', desc: 'hello@manhwa.readdy.site' },
  { icon: 'ri-chat-3-line', title: '合作与推荐', desc: '欢迎推荐优质韩漫，或洽谈内容合作' },
  { icon: 'ri-time-line', title: '回复时间', desc: '工作日 48 小时内回复' },
];

export default function Contact() {
  useSeo({
    title: '联系我们 - 漫画韩漫网址',
    description:
      '联系我们提交韩漫推荐、使用反馈或合作需求，漫画韩漫网址编辑团队会在工作日内及时回复。',
  });

  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 蜜罐检测：若被机器人填了值，不提交，并伪装成正常成功
    const honeypot = (fd.get('contact_alt') as string | null)?.trim() ?? '';
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }

    setSubmitting(true);
    setStatus('idle');
    setFormError('');

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === 'contact_alt') return;
      payload.append(key, String(value));
    });

    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '';
      const isSpam = /spam|form data is spam/i.test(responseText + serverMsg);
      const ok = res.ok && parsed?.code === 'OK' && !isSpam;

      if (ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(
          serverMsg && !isSpam
            ? serverMsg
            : '提交未成功，请稍后重试，或更换留言内容后再试。'
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络连接异常，请稍后重试。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ContactPage',
            name: '联系我们 - 漫画韩漫网址',
            url: 'https://suspensek.github.io/contact',
            description: '通过联系表单提交韩漫推荐、反馈与合作需求。',
          }),
        }}
      />
      <Navbar />

      <main className="pt-24 md:pt-32 pb-16 md:pb-24 bg-background-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav aria-label="面包屑导航" className="mb-8">
            <ol className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
              <li>
                <Link to="/" className="hover:text-primary-600 whitespace-nowrap">首页</Link>
              </li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
              <li aria-current="page" className="text-foreground-800 whitespace-nowrap">联系我们</li>
            </ol>
          </nav>

          <header className="max-w-2xl mb-12">
            <p className="text-xs font-medium tracking-widest text-accent-600 mb-3">CONTACT</p>
            <h1 className="font-heading text-3xl md:text-5xl text-foreground-950">联系我们</h1>
            <p className="mt-5 text-foreground-600 leading-relaxed">
              无论是推荐你心中的宝藏韩漫，还是反馈使用体验、洽谈合作，都欢迎通过下方表单告诉我们。你的每一条意见都会让漫画韩漫网址变得更好。
            </p>
          </header>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            <div className="lg:col-span-4">
              <div className="space-y-4">
                {contactInfo.map((c) => (
                  <div key={c.title} className="flex gap-4 bg-background-100/70 border border-background-200 rounded-2xl p-5">
                    <span className="w-11 h-11 shrink-0 rounded-xl bg-primary-100 text-primary-700 flex items-center justify-center">
                      <i className={`${c.icon} text-lg`} aria-hidden="true"></i>
                    </span>
                    <div>
                      <h2 className="font-medium text-foreground-950 text-sm">{c.title}</h2>
                      <p className="mt-1 text-sm text-foreground-600 break-all">{c.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-8">
              <div className="bg-background-100/70 border border-background-200 rounded-3xl p-6 md:p-8">
                {status === 'success' && (
                  <div className="mb-6 flex items-start gap-3 rounded-2xl bg-primary-100 border border-primary-200 p-4">
                    <i className="ri-checkbox-circle-fill text-primary-600 text-lg mt-0.5" aria-hidden="true"></i>
                    <div>
                      <p className="font-medium text-primary-800 text-sm">提交成功！</p>
                      <p className="mt-1 text-xs text-primary-700/80">感谢你的留言，我们会在工作日尽快回复。</p>
                    </div>
                  </div>
                )}

                {status === 'error' && (
                  <div className="mb-6 flex items-start gap-3 rounded-2xl bg-accent-100 border border-accent-200 p-4">
                    <i className="ri-error-warning-fill text-accent-600 text-lg mt-0.5" aria-hidden="true"></i>
                    <p className="text-sm text-accent-800">{formError}</p>
                  </div>
                )}

                <form id="contact-form" data-readdy-form="联系我们表单" onSubmit={handleSubmit} className="space-y-5" noValidate={false}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-2">姓名</label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        autoComplete="name"
                        placeholder="你的称呼"
                        className="w-full h-11 px-4 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-2">邮箱</label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        autoComplete="email"
                        placeholder="you@example.com"
                        className="w-full h-11 px-4 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-800 mb-2">主题</label>
                    <input
                      id="subject"
                      name="subject"
                      type="text"
                      placeholder="例如：推荐一部恋爱韩漫"
                      className="w-full h-11 px-4 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-2">留言内容</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="请写下你想说的话，最多 500 字"
                      className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none"
                    ></textarea>
                    <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
                  </div>

                  {/* 防垃圾邮件蜜罐字段 */}
                  <div className="hp-hide" aria-hidden="true">
                    <label htmlFor="contact_alt">请勿填写</label>
                    <input id="contact_alt" name="contact_alt" type="text" tabIndex={-1} autoComplete="off" readOnly />
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-1">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="h-11 px-7 rounded-full bg-primary-500 text-background-50 text-sm font-medium flex items-center justify-center gap-2 whitespace-nowrap hover:bg-primary-600 disabled:opacity-60 transition-colors"
                    >
                      {submitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i> 提交中...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line" aria-hidden="true"></i> 提交留言
                        </>
                      )}
                    </button>
                    <p className="text-xs text-foreground-500">我们承诺对你的信息严格保密，仅用于回复。</p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <BackToTop />
    </>
  );
}